import { useState } from 'react';
import { Loader2, RefreshCw, Shield, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const CHECK_LABELS = {
  endpoint_auth_coverage: 'Endpoint Auth Coverage',
  app_level_auth: 'App-Level Auth',
  secrets_at_rest: 'Secrets at Rest',
  secrets_in_logs: 'Secrets in Logs/Code',
  cors: 'CORS',
  security_headers: 'Security Headers & Transport',
  webhook_verification: 'Webhook Signature Verification',
  dependency_vulnerabilities: 'Dependency Vulnerabilities',
  auth_rate_limiting: 'Auth Rate Limiting',
};

const CHECK_ORDER = [
  'endpoint_auth_coverage',
  'app_level_auth',
  'secrets_at_rest',
  'secrets_in_logs',
  'cors',
  'security_headers',
  'webhook_verification',
  'dependency_vulnerabilities',
  'auth_rate_limiting',
];

/** Checks that carry a `detail` object with env/key metadata. */
const DETAIL_CHECKS = ['secrets_at_rest', 'webhook_verification'];

function StatusPill({ status }) {
  if (status === 'PASS') {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        <ShieldCheck className="h-3 w-3 mr-1" />
        PASS
      </Badge>
    );
  }
  if (status === 'FAIL') {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        <ShieldAlert className="h-3 w-3 mr-1" />
        FAIL
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
      <ShieldQuestion className="h-3 w-3 mr-1" />
      UNKNOWN
    </Badge>
  );
}

/** Render env/key detail metadata for secrets_at_rest and webhook_verification. */
function DetailBadges({ detail }) {
  if (!detail) return null;
  const items = [];
  if (detail.environment) {
    items.push(
      <Badge key="env" variant="outline" className="text-[10px] font-mono">
        env={detail.environment}
      </Badge>
    );
  }
  if (detail.key_source) {
    const color = detail.key_source === 'dedicated'
      ? 'bg-green-50 text-green-700'
      : detail.key_source === 'jwt_fallback'
        ? 'bg-yellow-50 text-yellow-700'
        : 'bg-red-50 text-red-700';
    items.push(
      <Badge key="ks" className={`text-[10px] font-mono ${color}`}>
        key={detail.key_source}
        {detail.key_id ? ` (kid=${detail.key_id})` : ''}
      </Badge>
    );
  }
  if (detail.smoobu_secret_set !== undefined) {
    items.push(
      <Badge key="smo" variant="outline" className="text-[10px] font-mono">
        smoobu={detail.smoobu_secret_set ? 'set' : 'unset'}
      </Badge>
    );
  }
  if (detail.uplisting_token_set !== undefined) {
    items.push(
      <Badge key="upl" variant="outline" className="text-[10px] font-mono">
        uplisting={detail.uplisting_token_set ? 'set' : 'unset'}
      </Badge>
    );
  }
  return items.length > 0 ? (
    <div className="flex flex-wrap gap-1 mt-0.5">{items}</div>
  ) : null;
}

export function SecurityPosturePanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.securityPosture();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const unprotectedRoutes = data?.checks?.endpoint_auth_coverage?.unprotected_routes || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Security Posture
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={runAudit} disabled={loading} size="sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : data ? (
            <RefreshCw className="h-4 w-4 mr-2" />
          ) : null}
          {data ? 'Re-run' : 'Run audit'}
        </Button>

        {error && (
          <div className="text-sm text-red-500">{error}</div>
        )}

        {data && (
          <>
            {/* Summary */}
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">Summary:</span>
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                {data.summary.pass} pass
              </Badge>
              {data.summary.fail > 0 && (
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                  {data.summary.fail} fail
                </Badge>
              )}
              {data.summary.unknown > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                  {data.summary.unknown} unknown
                </Badge>
              )}
            </div>

            {/* Check rows */}
            <div className="border rounded-md divide-y">
              {CHECK_ORDER.map((key) => {
                const check = data.checks[key];
                if (!check) return null;
                const showDetail = DETAIL_CHECKS.includes(key);
                return (
                  <div key={key} className="flex items-start gap-3 px-3 py-2">
                    <div className="pt-0.5 shrink-0">
                      <StatusPill status={check.status} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{CHECK_LABELS[key]}</div>
                      <div className="text-xs text-muted-foreground break-words">
                        {check.evidence}
                      </div>
                      {showDetail && <DetailBadges detail={check.detail} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Unprotected routes list */}
            {unprotectedRoutes.length > 0 && (
              <div className="space-y-1">
                <div className="text-sm font-medium text-red-700">
                  Unprotected Routes ({unprotectedRoutes.length})
                </div>
                <div className="border rounded-md bg-red-50 p-2 max-h-48 overflow-y-auto">
                  {unprotectedRoutes.map((route, i) => (
                    <div key={i} className="text-xs font-mono text-red-800">
                      {route}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dependency vulnerability counts */}
            {data.checks.dependency_vulnerabilities?.counts && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Dependency Audit Counts</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(data.checks.dependency_vulnerabilities.counts).map(([tool, counts]) => (
                    <div key={tool} className="border rounded-md p-2 text-xs">
                      <div className="font-medium uppercase">{tool}</div>
                      {counts.available ? (
                        <div className="text-muted-foreground">
                          {counts.total} total, {counts.critical} critical, {counts.high} high
                        </div>
                      ) : (
                        <div className="text-muted-foreground">{counts.note || 'unavailable'}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
